import React, { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { LocalizationProvider, MobileDateTimePicker } from '@mui/lab';
import {
  Autocomplete,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
} from '@mui/material';
import {
  Close as CloseIcon,
  DateRange as DateRangeIcon,
} from '@mui/icons-material';
import moment, { Moment } from 'moment';
import AdapterMoment from '@mui/lab/AdapterMoment';

import UserListDropdown, {
  TUserSelectOption,
} from '../../../../../../../components/Inputs/admin/UserListDropdown';
import {
  defaultTextFieldProps,
  TPAutoCompleteArrowIconComponent,
  TPTextField,
} from '../../../../../../../components/Inputs';
import {
  currencyOptions,
  ICurrencyOption,
} from '../../../../../../../constants';
import DarkLoadingButton, {
  defaultButtonStyles,
} from '../../../../../../../components/V2/Buttons';
import {
  adminCreateManualAdjustment,
  IManualAdjustment,
  TAdminCreateManualAdjustment,
} from '../../../../../../../store/V2/ManualAdjustmentsSlice';
import { IUser } from '../../../../../../../store/UserSlice';
import { getShortName } from '../../../../../../../helper';

interface AddManualAdjustmentModalProps {
  open: boolean;
  manualAdjustment?: IManualAdjustment;
  readOnly?: boolean;
  onClose: () => void;
}

export type TState = {
  selectedTutor: TUserSelectOption | null;
  selectedDate: Moment | null;
  title: string;
  description: string;
  selectedCurrency: ICurrencyOption | null;
  amount: string;
  isPaid: boolean;
};

const initialState: TState = {
  selectedTutor: null,
  selectedDate: null,
  title: '',
  description: '',
  selectedCurrency: null,
  amount: '',
  isPaid: true,
};

const AddManualAdjustmentModal: FunctionComponent<AddManualAdjustmentModalProps> = ({
  open,
  readOnly = false,
  manualAdjustment,
  onClose,
}) => {
  const dispatch = useDispatch();

  // State
  const [state, setState] = useState({ ...initialState });
  const [isLoading, setLoading] = useState(false);

  const isSubmitDisabled =
    !state.selectedTutor ||
    !state.selectedDate ||
    !state.title.trim().length ||
    !state.description.trim().length ||
    !state.selectedCurrency ||
    !state.amount.trim().length ||
    Number.isNaN(parseFloat(state.amount));

  let mode: 'view' | 'new' = 'new';

  if (readOnly && manualAdjustment) {
    mode = 'view';
  }

  useEffect(() => {
    if (mode !== 'new' && manualAdjustment) {
      const _tutor = manualAdjustment.tutor as IUser;

      setState((prevState) => {
        const newState = { ...prevState };

        if (_tutor) {
          newState.selectedTutor = {
            label: getShortName(_tutor.firstname, _tutor.lastname, true),
            value: _tutor._id,
          };
        }

        if (manualAdjustment.date) {
          newState.selectedDate = moment(manualAdjustment.date);
        }

        if (manualAdjustment.title) {
          newState.title = manualAdjustment.title;
        }

        if (manualAdjustment.description) {
          newState.description = manualAdjustment.description;
        }

        if (manualAdjustment.currency) {
          const _matchingItem = currencyOptions.find(
            (item) => item.value === manualAdjustment.currency
          );

          if (_matchingItem) {
            newState.selectedCurrency = { ..._matchingItem };
          }
        }

        newState.amount =
          typeof manualAdjustment.amount === 'number'
            ? manualAdjustment.amount.toString()
            : '';

        return newState;
      });
    }
  }, []);

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({
      ...prevState,
      ...attributes,
    }));
  };

  const handleSubmit = async () => {
    if (state.selectedTutor && state.selectedDate && state.selectedCurrency) {
      setLoading(true);

      const apiData: TAdminCreateManualAdjustment = {
        tutorId: state.selectedTutor.value,
        date: state.selectedDate.toISOString(),
        title: state.title.trim(),
        descritpion: state.description.trim(),
        currency: state.selectedCurrency.value,
        amount: parseFloat(state.amount),
        isPaid: state.isPaid,
      };

      const res: any = await dispatch(adminCreateManualAdjustment(apiData));

      setLoading(false);

      if (res && res.payload) {
        onClose();
      }
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { borderRadius: '20px' } }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: { xs: '15px', sm: '30px' },
          color: '#000000',
          fontWeight: 600,
          fontSize: '16px',
        }}
      >
        New Adjustment
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: { xs: '7px', sm: '22px' },
          top: { xs: '7px', sm: '22px' },
        }}
      >
        <CloseIcon sx={{ color: '#000000' }} />
      </IconButton>
      <DialogContent
        sx={{ p: { xs: '0 15px 15px 15px', sm: '0 30px 30px 30px' } }}
      >
        <div>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <div style={{ minHeight: '6px' }} />
                <FormControl fullWidth>
                  <UserListDropdown
                    userTypes={['tutor']}
                    selectedItem={state.selectedTutor}
                    textFieldProps={{
                      label: 'Tutor *',
                      placeholder: 'Select',
                      ...defaultTextFieldProps,
                    }}
                    componentProps={{ readOnly }}
                    handleChange={(e, value) =>
                      updateStateAttributes({
                        selectedTutor: value,
                      })
                    }
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <MobileDateTimePicker
                    clearable
                    readOnly={readOnly}
                    value={state.selectedDate}
                    label="Date of the record *"
                    inputFormat="YYYY-MM-DD hh:mm A"
                    maxDateTime={moment()}
                    onChange={(date) =>
                      updateStateAttributes({
                        selectedDate: date,
                      })
                    }
                    renderInput={(params) => (
                      <TPTextField
                        {...params}
                        {...defaultTextFieldProps}
                        size="small"
                        placeholder="Select"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <InputAdornment position="end">
                              <DateRangeIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <TPTextField
                    {...defaultTextFieldProps}
                    size="small"
                    label="Adjustment Title *"
                    placeholder="Enter here..."
                    value={state.title}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ readOnly }}
                    onChange={(event) =>
                      updateStateAttributes({ title: event.target.value })
                    }
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <TPTextField
                    multiline
                    rows={2}
                    size="small"
                    label="Adjustment Description *"
                    placeholder="Enter here..."
                    value={state.description}
                    InputProps={{ readOnly }}
                    InputLabelProps={{ shrink: true }}
                    onChange={(event) =>
                      updateStateAttributes({ description: event.target.value })
                    }
                  />
                </FormControl>
              </Grid>
              <Grid item xs={4}>
                <FormControl fullWidth>
                  <Autocomplete
                    size="small"
                    readOnly={readOnly}
                    value={state.selectedCurrency}
                    options={currencyOptions}
                    popupIcon={TPAutoCompleteArrowIconComponent}
                    getOptionKey={(option) => option.value}
                    isOptionEqualToValue={(option, value) =>
                      option.label === value.value
                    }
                    onChange={(event, value) =>
                      updateStateAttributes({ selectedCurrency: value })
                    }
                    renderInput={(params) => (
                      <TPTextField
                        {...params}
                        {...defaultTextFieldProps}
                        size="small"
                        label="Currency *"
                        placeholder="Select"
                      />
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={8}>
                <FormControl fullWidth>
                  <TPTextField
                    {...defaultTextFieldProps}
                    size="small"
                    label="Amount *"
                    placeholder="Enter here..."
                    value={state.amount}
                    InputProps={{ readOnly }}
                    InputLabelProps={{ shrink: true }}
                    onChange={(event) =>
                      updateStateAttributes({ amount: event.target.value })
                    }
                  />
                </FormControl>
              </Grid>
            </Grid>
          </LocalizationProvider>
        </div>
      </DialogContent>
      {mode !== 'view' ? (
        <DialogActions sx={{ p: { xs: '15px', sm: '30px' } }}>
          <DarkLoadingButton
            disableElevation
            color="inherit"
            variant="contained"
            onClick={handleSubmit}
            loading={isLoading}
            disabled={isSubmitDisabled}
            sx={{ ...defaultButtonStyles }}
          >
            Add
          </DarkLoadingButton>
        </DialogActions>
      ) : null}
    </Dialog>
  );
};

export default AddManualAdjustmentModal;
